'use client';

import { useEffect, useRef, useState, type Ref } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isUnconfirmedStatus } from '@/lib/command-ack';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { droneCommandAvailability, type CommandRoute } from '@/lib/drone-command-availability';
import {
  isCommandKeyVisible,
  visibleCommandKeys,
  type BarKeyAction,
} from '@/lib/command-bar-phase';
import { landingStateView } from '@/lib/landing-state';
import { AltitudeControl } from '@/components/dashboard/AltitudeControl';
import {
  evaluateCeiling,
  formatAltitude,
  type AltitudeProfile,
  type AltitudeUnit,
} from '@/lib/altitude';
import type { AltitudeProfileStatus } from '@/hooks/useAltitudeProfile';
import type { DroneCommandAction, DroneCommandController } from '@/hooks/useDroneCommand';
import {
  MANUAL_GROUND_VERBS_SUSPENDED,
  type ManualControlController,
} from '@/hooks/useManualControl';
import { MANUAL_REFUSAL } from '@/lib/manual-vector';
import type { LiveDrone } from '@/hooks/useWebSocket';
import {
  COMMAND_KEY_LABEL,
  CONFIRM_COPY,
  CONFIRM_DIALOG,
  DIRECT_VARIANT,
  HELD_ARM_PROMPT,
} from '@/lib/copy';

/**
 * Level two's other half: what you are about to do to the selected aircraft.
 *
 * The bar carries no telemetry. Every number lives on the state card, so the
 * operator never has to decide which of two readouts is authoritative. What the
 * bar does show is the command target — pressing RTH on the wrong aircraft is
 * the expensive mistake — and the lifecycle of the command in flight.
 *
 * Key positions are fixed regardless of which drone is selected, so muscle
 * memory survives switching aircraft.
 *
 * Since #505 slice 10 they are NOT fixed across an aircraft's own phases: the
 * bar renders the verbs the phase can accept, so a key leaving the row moves the
 * keys beside it. The guarantee that survives is narrower and deliberate - the
 * key group is right-anchored (`ml-auto`), so the row's RIGHT end is a fixed
 * screen position and ABORT and DETAILS never move. That is where the two verbs
 * an operator reaches for without reading are, which is the whole of D14's
 * argument. ARM and DISARM are exempt from the filter for the same reason
 * (D46): they sit at opposite ends of the row, so filtering them re-laid it out
 * between the two presses of ARM then TAKEOFF.
 */

/**
 * Irreversible enough to be worth a confirm naming the aircraft.
 *
 * ABORT is deliberately NOT in this set. It is the key an operator reaches for
 * when something is already going wrong, and its outcome — stop and hold — is
 * the safe state, not a destructive one. A dialog between the press and the
 * hold costs seconds exactly when they are worth the most, and an operator who
 * has to confirm an emergency stop learns to hesitate before pressing it. The
 * cost of an unintended ABORT is a hovering aircraft; the cost of a slow one is
 * the aircraft.
 */
const CONFIRM_COMMANDS: ReadonlySet<DroneCommandAction> = new Set([
  'takeoff', 'return_home', 'land', 'confirm_landing',
]);

/**
 * What the API commands when the client sends no altitude
 * (`executor.takeoff(altitude_m ?? 10)` in src/api/drones.ts). Mirrored here so
 * a dialog that cannot offer a picker can still name the number that will fly.
 */
const FALLBACK_TAKEOFF_ALTITUDE_M = 10;

/**
 * The smoke-test hop: motors up, a low hover, nothing else.
 *
 * Deliberately below the companion app's wayline threshold (3 m,
 * WAYLINE_TAKEOFF_MIN_ALT_M in MissionController.kt) so a DJI takes the plain
 * KeyStartTakeoff branch — no wayline upload, no mission row, just the
 * aircraft's own fixed ~1.2 m auto-takeoff hover. MAVLink flies it literally
 * as NAV_TAKEOFF to this altitude. Still ARM-gated: the app holds every motor
 * command until the operator presses ARM.
 */
const HOP_ALTITUDE_M = 1.2;

// The words, and the reasoning behind each one, are in `lib/copy/commands.ts`.

// `BarKeyAction` now lives in `lib/command-bar-phase.ts`, beside the rule that
// decides which of these keys the aircraft's phase renders.

/**
 * The confirm-landing sentence, with the aircraft's own reason when it gave one.
 *
 * The honest fallback is still needed and still says so: only the DJI companion
 * plane reports a protection state, so on every other airframe SHOVRON genuinely
 * cannot see why a descent stopped.
 */
function confirmLandingCopy(landingReason: string | null): string {
  const base = CONFIRM_COPY.confirm_landing as string;
  return landingReason === null
    ? `${base} SHOVRON has no reason from the aircraft for why it stopped.`
    : `${base} The aircraft reports ${landingReason}.`;
}

/** How much of the RETURN HOME refusal reason fits beside "Emergency RTH" (#586). */
const RTH_REFUSAL_MAX_CHARS = 44;

/**
 * The fixed marker `src/api/drones.ts` and `src/services/mission-stop.ts` both append
 * right after a refusal's own reasons, before either one's advisory sentence.
 *
 * Splitting on THIS, not on the first ". ", is load-bearing: a reason can itself end in
 * a full sentence (`evaluateLandingGoNoGo`'s low-battery reason does, telling the
 * operator "Land directly instead" mid-reason) and cutting at the first period would
 * lose everything after it, including a second `; `-joined reason.
 */
const RTH_REFUSAL_REASONS_MARKER = '. The aircraft was NOT commanded to return';

/**
 * Cut `text` to `maxChars` on a word boundary rather than mid-word, appending an
 * ellipsis. A mid-word cut ("...emergency mode - clear it be…") reads as garbled on a
 * surface an operator has to scan in under three seconds; this keeps whole words.
 */
function truncateAtWord(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  const slice = text.slice(0, Math.max(0, maxChars - 1));
  const lastSpace = slice.lastIndexOf(' ');
  // Only back off to the last space if that still leaves most of the budget used -
  // otherwise a single long first word would truncate to almost nothing.
  const cut = lastSpace > maxChars * 0.6 ? slice.slice(0, lastSpace) : slice;
  return `${cut}…`;
}

/**
 * The RETURN HOME refusal reason, cut to fit on the EMERGENCY RTH control.
 *
 * The server can report more than one reason at once (`evaluateLandingGoNoGo` documents
 * itself as returning "one message per failed check"), `; `-joined into the same string
 * this pulls from. Truncating the joined string instead of just the first reason risks
 * losing a whole second reason with no indication anything was cut - this shows the
 * first reason on the button and names how many more there were, so nothing silently
 * disappears from the one surface guaranteed to be read. The full, un-truncated message
 * still rides on the control's `title` for a hover.
 */
function rthRefusalReason(message: string): string {
  const markerIndex = message.indexOf(RTH_REFUSAL_REASONS_MARKER);
  const reasonsText = markerIndex === -1 ? message : message.slice(0, markerIndex);
  const reasons = reasonsText.split('; ').filter((r) => r.trim().length > 0);
  const first = reasons[0] ?? reasonsText;
  const more = reasons.length > 1 ? ` (+${reasons.length - 1} more)` : '';
  return `${truncateAtWord(first, RTH_REFUSAL_MAX_CHARS - more.length)}${more}`;
}

/**
 * The bar's keys that the companion app HOLDS until an operator arms.
 *
 * Not takeoff alone: MissionController.kt routes `takeoff`, `start_mission` and
 * `goto` through the same `if (!_armed) holdMotorCommand(env)` branch, and
 * `_armed` is only ever set by an ARM — a COP press or the RC button. A Mavic
 * launched with the sticks (the normal way a Mavic flies) is therefore unarmed
 * in the air, so a GO-TO holds exactly like a takeoff does. `start_mission` is
 * not a key on this bar; the other two are.
 *
 * `start_orbit` is deliberately NOT here, and the reason is worth stating because it
 * is the same shape of hazard: an orbit can only be launched on an AIRBORNE aircraft,
 * which is exactly the state in which a Mavic is routinely unarmed, so it would hold
 * like a GO-TO. It is kept off this list because it is not a key on this bar — nothing
 * here can send it, so there would be no held key to light up — and because the launch
 * service sends the arm itself for an orbit on the companion path, which is where the
 * hold is actually prevented (`mission-launch.ts`). If an orbit ever DOES arrive held,
 * that fix belongs there, not here.
 */
const HELD_ON_ARM = ['takeoff', 'goto'] as const;

/**
 * What the ARM key says when it is the release for a held command.
 *
 * Per verb, because "authorize the launch" is a false description of a GO-TO on
 * an aircraft that is already airborne.
 */

interface KeyDef {
  action: BarKeyAction;
  label: string;
  hint?: string;
  danger?: boolean;
}

export interface DroneCommandBarProps {
  drone: LiveDrone;
  command: DroneCommandController;
  /**
   * The executor the API will route this drone's commands to, from
   * `commandRouteOf(registry row)`. Null while the registry is loading, after it
   * failed, or for a route name this frontend does not know — vendor gating is
   * suppressed then, so the bar never claims a false reason.
   *
   * Replaces the old `manufacturer` + `isCompanion` pair: the server owns the
   * routing rule, and two of them could disagree about the same aircraft.
   */
  commandRoute?: CommandRoute | null;
  /**
   * From `commandSecretPresentOf(registry row)` (#261). Null is unknown and
   * gates nothing; an explicit `false` means the RC app cannot sign, so every
   * command would be refused and the bar says so instead of offering keys.
   */
  commandSecretPresent?: boolean | null;
  dockId?: string | null;
  canOperate: boolean;
  /** True when the drone is running a mission, enabling HOLD/RESUME. */
  onMission?: boolean;
  /** Org altitude configuration: presets, ceiling, waiver, RTH, display unit. */
  altitudeProfile: AltitudeProfile;
  /** Label for whoever owns the ceiling, e.g. "FAA Part 107". Null if unknown. */
  ceilingAuthority: string | null;
  /** 'error' means the values above are platform defaults, not this org's. */
  altitudeProfileStatus?: AltitudeProfileStatus;
  /** "Fly here" pick mode is armed — the next map click repositions the drone. */
  gotoArmed?: boolean;
  /** Toggle "fly here" pick mode. Absent for drones that cannot goto. */
  onToggleGoto?: () => void;
  /** Manual flight authority for this aircraft. Absent → no MANUAL key at all. */
  manual?: ManualControlController;
  /** Why manual flight is refused right now (manualControlOffer), or null. */
  manualDisabledReason?: string | null;
  /** Take manual authority, or hand it back when it is already held. */
  onToggleManual?: () => void;
  /** Name of the mission in progress, so the confirm can name what it aborts. */
  missionName?: string | null;
  isDetailsOpen?: boolean;
  onOpenDetails: () => void;
  onOpenDock?: (dockId: string) => void;
  now?: number;
}

export function DroneCommandBar({
  drone,
  command,
  commandRoute = null,
  commandSecretPresent = null,
  dockId = null,
  canOperate,
  onMission = false,
  altitudeProfile,
  ceilingAuthority,
  altitudeProfileStatus = 'ready',
  gotoArmed = false,
  onToggleGoto,
  manual,
  manualDisabledReason = null,
  onToggleManual,
  missionName = null,
  isDetailsOpen = false,
  onOpenDetails,
  onOpenDock,
  now,
}: DroneCommandBarProps) {
  const [confirming, setConfirming] = useState<BarKeyAction | null>(null);

  // Kept true from the EMERGENCY RTH click to that send's resolution (#586).
  // `useDroneCommand` clears `postFailureOf('return_home')` synchronously the
  // moment a fresh attempt goes out (any action, direct or not), so without
  // this the button would unmount on the same tick as the click - a tap that
  // visibly does nothing is indistinguishable from one that missed.
  //
  // NEVER used to disable the button (see its render site): `api.post` carries
  // no timeout, and disabling on a hung POST would freeze the one control
  // named "Emergency" as an unusable dead button for as long as the browser
  // waits - the exact failure mode ABORT and LAND DIRECT are already built to
  // avoid (`reissuableWhileBusy`, this file). It only drives the spinner.
  const [emergencyRthSending, setEmergencyRthSending] = useState(false);

  // The last REAL refusal reason seen for this drone, held past the moment
  // `rthRefused` itself goes null (the instant a send starts, per the comment
  // above). Without this the two-line control collapses to one line for the
  // whole in-flight window of every attempt, including retries - a layout
  // shift during exactly the window the operator is watching most closely.
  const [lastRthRefusalMessage, setLastRthRefusalMessage] = useState<string | null>(null);

  // Which drone the click above was FOR, read inside the async continuation so
  // a stale send for a since-abandoned drone cannot clear the busy state of a
  // fresh Emergency RTH the operator has since fired for a newly selected one
  // (both are real `return_home:direct` POSTs; `useDroneCommand`'s own lock is
  // keyed by drone, so nothing stops them overlapping at the network level).
  const emergencyRthDroneIdRef = useRef(drone.drone_id);
  emergencyRthDroneIdRef.current = drone.drone_id;

  // This bar is not remounted per drone - the page keeps one instance and
  // re-points its `drone`/`command` props on selection - so state left over
  // from the PREVIOUS aircraft would otherwise keep painting a busy or
  // already-stale "Emergency RTH" over whichever drone is now selected.
  // `useDroneCommand` resets `postFailureOf` the same way on its own `droneId`
  // change; this keeps the local half of the same state in step with it.
  useEffect(() => {
    setEmergencyRthSending(false);
    setLastRthRefusalMessage(null);
  }, [drone.drone_id]);

  // Seeded from the org default every time the dialog opens (see `run`), never
  // carried over from the last flight: an altitude the operator chose for a
  // previous takeoff must not be re-flown silently on the next one.
  const [takeoffAltitudeM, setTakeoffAltitudeM] = useState(() => altitudeProfile.default_m);
  const [unit, setUnit] = useState<AltitudeUnit>(altitudeProfile.unit);

  // `/api/org` may resolve while the dialog is already open — the COP is the
  // landing page, so a cold load and a reach for TAKEOFF overlap routinely.
  // When it does, the presets, band and ceiling marker all switch to the org's
  // configuration; without this the one number that matters, the value about to
  // be flown, would be the only thing left on the platform default.
  const takeoffTouchedRef = useRef(false);
  useEffect(() => {
    if (confirming !== 'takeoff' || takeoffTouchedRef.current) return;
    setTakeoffAltitudeM(altitudeProfile.default_m);
    setUnit(altitudeProfile.unit);
  }, [confirming, altitudeProfile.default_m, altitudeProfile.unit]);

  // The card ticks at 1 Hz; without the same clock here the bar would freeze
  // showing "Commanding" and live keys while the card 60px above it has already
  // gone to LINK LOST. Two adjacent surfaces disagreeing about whether an
  // aircraft is reachable is exactly what the card/bar split exists to prevent.
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (now !== undefined) return;
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [now]);

  const callsign = drone.callsign?.trim() || drone.drone_id.slice(-6).toUpperCase();
  const availability = droneCommandAvailability({
    drone, commandRoute, commandSecretPresent, dockId, canOperate, now: now ?? tick,
  });

  // Why a commanded LAND is hanging, straight from the aircraft (#200). Null
  // when nothing has reported one - the chip is absent rather than reassuring.
  const landing = landingStateView(drone);

  // The orchestrated RETURN HOME's own landing-gate refusal (#586). Distinct
  // from `landing`: that chip is about a LAND already accepted and hanging on
  // the aircraft's own protection state; this is about a RETURN that never
  // reached the aircraft at all, because the dock sequence refused it before
  // dispatch. `code` narrows to the one refusal EMERGENCY RTH actually
  // answers - a different POST failure (permissions, a network error, or a
  // refusal the server marked unsafe to skip - `return_sequence_busy`, a bay
  // sequence genuinely running on the dock) must not offer an escape hatch
  // that has nothing to do with it, or does the one thing it must not.
  const returnPostFailure = command.postFailureOf('return_home');
  const rthRefused = returnPostFailure?.code === 'landing_sequence_refused' ? returnPostFailure : null;

  // Captured the instant a real refusal is seen, so the visible reason survives
  // the gap where `rthRefused` itself is null (see `lastRthRefusalMessage`'s
  // own declaration) - a ref would not trigger the re-render this needs.
  useEffect(() => {
    if (rthRefused) setLastRthRefusalMessage(rthRefused.message);
  }, [rthRefused]);

  // What the control actually shows: the LIVE refusal when there is one, the
  // last known one while a send is in flight and `rthRefused` has gone null,
  // falling back to the OR (not the effect above, which lags a render) so the
  // very first render of a fresh refusal shows its reason immediately.
  const displayedRthMessage = rthRefused?.message ?? lastRthRefusalMessage;

  // The one DJI path with a real executor. Derived from the route rather than
  // carried as its own prop, so the bar cannot hold a companion flag that
  // disagrees with the route it is gating on.
  const isCompanion = commandRoute === 'dji_companion';

  // The companion app is sitting on a motor command, waiting for an ARM — the
  // COP's ARM key is the release, so it must visibly ask to be pressed rather
  // than leave the operator staring at a held command wondering what it wants.
  // Derived straight from the ack state; no new client state machine.
  //
  // Every verb in HELD_ON_ARM, not takeoff alone. A held GO-TO used to hold in
  // silence: the ARM key stayed grey, the only account of the hold was a tooltip
  // on the HELD rung, and 60 s later (ARM_HOLD_MS) it went red having never left
  // the RC. Before UI-registered DJI aircraft got their command bar back, almost
  // no operator could reach that rung; now every Mavic in the fleet can.
  const heldAwaitingArm = HELD_ON_ARM.find((a) => command.statusOf(a) === 'held') ?? null;

  // HOLD and RESUME are separate keys rather than one toggle. Deriving "is
  // paused" from the ack lifecycle was wrong: acks clear after 5s, so a RESUME
  // key would silently revert to HOLD while the aircraft was still holding,
  // leaving no way to resume. Nothing in telemetry reports pause state, so the
  // bar does not pretend to know it.
  // Ordered by flight phase, left to right: prep, launch, in-flight, recover.
  // GO-TO is a mode toggle (arms map-click pick), not a one-shot — handled
  // specially in the render and keyboard path.
  // Manual authority is exclusive and it takes the flight keys, so the bar has
  // to say so on the keys themselves rather than only in the banner above.
  const manualHolds = manual?.holdsAuthority ?? false;

  const keys: KeyDef[] = [
    { action: 'arm', label: COMMAND_KEY_LABEL.arm, hint: 'A' },
    { action: 'takeoff', label: COMMAND_KEY_LABEL.takeoff, hint: 'T' },
    { action: 'goto', label: COMMAND_KEY_LABEL.goto, hint: 'G' },
    // Fixed position, immediately after GO-TO: the two map-owning modes sit
    // together and neither ever moves.
    ...(manual ? [{ action: 'manual' as const, label: COMMAND_KEY_LABEL.manual, hint: 'M' }] : []),
    { action: 'pause', label: COMMAND_KEY_LABEL.pause, hint: 'P' },
    { action: 'resume', label: COMMAND_KEY_LABEL.resume, hint: 'O' },
    { action: 'return_home', label: COMMAND_KEY_LABEL.return_home, hint: 'R', danger: true },
    { action: 'land', label: COMMAND_KEY_LABEL.land, hint: 'L', danger: true },
    // LAND's follow-up, immediately after it. Not `danger`: it completes a
    // descent the operator already commanded, and the destructive-looking key
    // beside it is the one that starts the descent. Sits here rather than at
    // the end so the recover group reads in the order it is flown.
    { action: 'confirm_landing', label: COMMAND_KEY_LABEL.confirm_landing, hint: 'C' },
    { action: 'disarm', label: COMMAND_KEY_LABEL.disarm },
  ];

  /**
   * Whether a takeoff altitude this bar sends is one the aircraft actually flies.
   *
   * Only the MAVLink plane honours it — the Pi bridge turns `altitude_m` into
   * MAV_CMD_NAV_TAKEOFF. The DJI companion calls `KeyStartTakeoff`, which takes
   * no target at all and climbs to the aircraft's own fixed altitude, so a
   * picker there would set a number the Mavic never flies.
   *
   * Gated on knowing the route for the same reason the vendor checks are: with no
   * route the vendor is unknown, and a dialog that promises an altitude it cannot
   * attribute is exactly the false claim this surface exists to avoid. Unknown
   * vendor falls back to the generic copy and sends nothing.
   */
  // MAVLink flies it via NAV_TAKEOFF; the DJI companion now flies it as a
  // wayline climb (plain DJI auto-takeoff ignores altitude - nine field
  // flights hovered at 1.2 m to prove it). Unknown vendor still sends nothing.
  const altitudeSettable = commandRoute !== null;

  // Advisory only — evaluateCeiling never blocks a launch, because an operator
  // may hold authority the platform has no record of.
  const ceilingNotice = evaluateCeiling(
    takeoffAltitudeM,
    { ...altitudeProfile, unit },
    ceilingAuthority,
  );

  // The confirm key states the altitude it is about to fly, and changes colour
  // when that altitude is over the ceiling, so the decision is on the control
  // the operator actually presses rather than only in text above it.
  const confirmOverCeiling =
    confirming === 'takeoff' && altitudeSettable && ceilingNotice.status !== 'ok';
  // The key NAMES the command it is about to send. It used to say "Confirm", which is the
  // one thing every confirm button in every product says and the one thing this one did not
  // need to: the operator has already read a sentence explaining what will happen, and the
  // press is the moment to say which verb it is.
  const confirmLabel =
    confirming === 'manual'
      ? 'Take control'
      : confirming === 'takeoff' && altitudeSettable
        ? `${confirmOverCeiling ? 'Above ceiling' : COMMAND_KEY_LABEL.takeoff} · ${formatAltitude(takeoffAltitudeM, unit)}`
        : confirming && confirming in COMMAND_KEY_LABEL
          ? COMMAND_KEY_LABEL[confirming as keyof typeof COMMAND_KEY_LABEL]
          : 'Send';

  /**
   * Taking manual control of an aircraft that is flying a mission aborts the
   * mission — the RC switches to virtual stick and the wayline stops. That is a
   * destructive side effect on work somebody planned, so it is confirmed and the
   * mission is NAMED: "this will abort" without saying what is exactly the
   * dialog an operator learns to click through.
   *
   * Handing authority back is never confirmed. It reduces risk.
   */
  const runManual = () => {
    if (!onToggleManual) return;
    if (!manualHolds && onMission) {
      setConfirming('manual');
      return;
    }
    onToggleManual();
  };

  const run = (action: BarKeyAction) => {
    if (action === 'manual') {
      runManual();
      return;
    }
    if (CONFIRM_COMMANDS.has(action)) {
      // Re-seed here rather than in an effect so the dialog can never open
      // showing the previous takeoff's altitude for a frame.
      if (action === 'takeoff') {
        setTakeoffAltitudeM(altitudeProfile.default_m);
        setUnit(altitudeProfile.unit);
        takeoffTouchedRef.current = false;
      }
      setConfirming(action);
      return;
    }
    void command.send(action);
  };

  /**
   * Is this command still in flight? (#344)
   *
   * `command.submitting` alone is only the POST window, and on hardware a 202
   * comes back in a couple of hundred milliseconds - so one press fired the same
   * flight command four times in 0.9 s while the key looked live between POSTs.
   * The window an operator means by "in flight" is the ack lifecycle, which the
   * hook already tracks: pending until the device answers.
   *
   * The hook refuses a duplicate send outright; this is what makes the refusal
   * VISIBLE, so a key that will do nothing looks like a key that will do nothing.
   */
  const inFlight = (action: DroneCommandAction): boolean =>
    command.submitting === action || command.phaseOf(action) === 'pending';

  /**
   * The keys this phase renders (#505 slice 10).
   *
   * A verb the phase can NEVER accept comes off the bar; a verb it owns that
   * something is momentarily blocking stays, greyed, carrying its refusal
   * through `disabledReason` below (D13). RTH and ABORT are exempt from the
   * whole rule and render in every phase (D14).
   *
   * The inputs are the four states that must never have a control removed from
   * under them, and the rule itself is in `lib/command-bar-phase.ts` with its
   * reasoning per verb.
   */
  const phaseInputs = {
    drone,
    gotoArmed,
    heldAwaitingArm,
    isInFlight: inFlight,
  };
  const visibleKeys = visibleCommandKeys(keys, phaseInputs);

  const disabledReason = (action: BarKeyAction): string | null => {
    if (action === 'manual') {
      // A held session is always releasable; a refusal only ever blocks TAKING
      // control, never giving it back.
      if (manualHolds) return null;
      if (!onToggleManual) return 'Manual flight is not wired up on this screen.';
      // A refusal nothing on this screen can clear outranks one that is merely
      // waiting on the operator. Telling somebody to cancel Fly here so they can
      // take manual control, when manual control is not on offer at all, sends
      // them to give up a pick mode for nothing and answers the wrong question.
      if (manualDisabledReason === MANUAL_REFUSAL.notReleased) return manualDisabledReason;
      if (gotoArmed) return 'Fly here is waiting for a map click. Cancel it before taking manual control.';
      return manualDisabledReason;
    }
    // ARM and DISARM are ground verbs. While the operator is flying the aircraft
    // by hand, A and D are strafe keys and the two buttons would be a way to cut
    // the motors mid-flight. RTH, LAND and ABORT stay live in every state — a
    // command that reduces risk is never blocked by an authority lock.
    if (manualHolds && (action === 'arm' || action === 'disarm')) {
      return MANUAL_GROUND_VERBS_SUSPENDED;
    }
    if (manualHolds && action === 'goto') {
      return 'Manual control is live. Release it before arming Fly here.';
    }
    if (availability.unsupported.has(action)) return availability.unsupportedReason;
    if ((action === 'pause' || action === 'resume') && !onMission) {
      return 'No mission running to hold.';
    }
    return null;
  };

  // The MANUAL key's own refusal, rendered as text beside the lifecycle readout.
  // Null when there is no MANUAL key, or when it is pressable — a release is
  // never refused, so a held session never produces one.
  const manualKeyRefusal = manual ? disabledReason('manual') : null;

  // Amber on this bar means DEGRADED — a low battery, a landing stuck on a busy
  // bay, something an operator is meant to act on. "Manual flight is not
  // released yet" is none of those: it is on every aircraft, in every phase, and
  // it never clears. Left amber it would be the one warning on the bar that is
  // permanently lit, which is how operators learn to discount the colour on the
  // bar where a genuinely degraded LAND has to be seen. Muted says "off by
  // design" and keeps amber worth looking at.
  const manualRefusalIsPermanent = manualKeyRefusal === MANUAL_REFUSAL.notReleased;

  // The keys render their shortcut, so the shortcut has to exist. An operator
  // who learns "R is RTH" and gets nothing in an emergency is worse off than
  // one who never saw the hint.
  //
  // Guards, in order: never on a key the OS is auto-repeating, never while
  // typing, never while the confirm dialog owns the keyboard, never for a drone
  // that cannot be commanded, and never for a key whose button is disabled —
  // pressing a shortcut must do exactly what clicking its key does, including
  // nothing.
  //
  // ABORT is the one exception, and only to the dialog guard: an emergency stop
  // is not something a confirm the operator has already stopped caring about
  // gets to hold up. It still yields to the typing guard, because ⇧⌫ in the
  // altitude field is a delete.
  const runRef = useRef(run);
  runRef.current = run;
  const disabledRef = useRef(disabledReason);
  disabledRef.current = disabledReason;
  // A shortcut must do exactly what clicking its key does, including nothing
  // when the key is not on the bar at all (#505 slice 10). Behind a ref like
  // the others: this listener is not re-subscribed on every telemetry tick, so
  // a captured predicate would answer for the phase the aircraft was in when
  // the effect last ran - which is the one state where being wrong matters,
  // since the phase changed underneath it.
  const visibleKeyRef = useRef((action: BarKeyAction) => isCommandKeyVisible(action, phaseInputs));
  visibleKeyRef.current = (action: BarKeyAction) => isCommandKeyVisible(action, phaseInputs);
  const onToggleGotoRef = useRef(onToggleGoto);
  onToggleGotoRef.current = onToggleGoto;
  const onToggleManualRef = useRef(onToggleManual);
  onToggleManualRef.current = onToggleManual;
  const manualRef = useRef(manual);
  manualRef.current = manual;
  const inFlightRef = useRef(inFlight);
  inFlightRef.current = inFlight;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Held keys auto-repeat at the OS rate. Every verb here is one-shot or a
      // toggle, so a held key means one command, not thirty — and ABORT, the
      // key an operator is most likely to lean on in a panic, no longer has a
      // confirm dialog absorbing the repeats.
      if (e.repeat) return;

      const target = e.target as HTMLElement | null;
      // A widget that owns the keyboard for its whole subtree, not only for the
      // text field inside it. Keying off tagName alone left every other
      // focusable in such a widget — a clear button, a result row — issuing
      // flight commands to the selected aircraft. Scoped rather than adding
      // BUTTON globally, which would stop the command keys working right after
      // the operator clicks a command key.
      //
      // The `instanceof` is load-bearing: with focus on the body this listener
      // fires with `document` as the target, which has no `closest`.
      const inTextScope = target instanceof Element
        && target.closest('[data-cop-text-scope]') !== null;
      if (
        target
        && (target.isContentEditable
          || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
          || inTextScope)
      ) {
        return;
      }

      let action: DroneCommandAction | null = null;
      // Checked before the open-dialog guard below, so the emergency stop is
      // the one key a confirm dialog cannot swallow. An operator who opens
      // TAKEOFF, sees something go wrong and reaches for ABORT must not have to
      // find Cancel first — the dialog is modal, so its overlay has already
      // taken the mouse away from them.
      if (e.key === 'Backspace' && e.shiftKey) action = 'abort';
      // STOP, offered to the flight keys BEFORE the dialog guard, exactly as
      // ABORT is and for the identical reason. With a LAND or RTH confirm open
      // while manual authority is held, SPACE used to do nothing at all — and
      // the on-map STOP was behind the dialog's own modal overlay, so the
      // operator had no way to stop a moving aircraft without first finding
      // Cancel. `handleKeyDown` returns false unless authority is actually
      // held, so nothing changes when no session exists.
      else if (
        (e.key === ' ' || e.key.toLowerCase() === 'spacebar')
        && manualRef.current?.handleKeyDown(e)
      ) {
        // Also stops the space from activating whichever dialog button has
        // focus: a STOP must not double as a Cancel.
        e.preventDefault();
        return;
      }
      else if (confirming !== null) return;
      // While manual authority is held the flight keys own the keyboard, and
      // they are offered the event HERE rather than from a second global
      // listener — two listeners racing for the same keydown is how one key ends
      // up doing two things. `handleKeyDown` returns true only for keys it
      // actually maps (W/S/A/D translate, C/Z climb, Q/E yaw, Space stop, Esc
      // stop-and-release, 1-3 speed), so R, L and ABORT are never in its way.
      //
      // This is also what suspends ARM (A) and DETAILS (D) for exactly as long
      // as somebody is flying — they are ground verbs, and the contract takes
      // those two letters for the strafe axis.
      else if (manualRef.current?.handleKeyDown(e)) {
        e.preventDefault();
        return;
      }
      else if (!e.shiftKey) {
        const key = e.key.toLowerCase();
        if (key === 'd') {
          e.preventDefault();
          onOpenDetails();
          return;
        }
        if (!availability.canCommand) return;
        // A mode toggle, not a one-shot: it takes an exclusive authority lock,
        // and taking it during a mission opens the confirm that names the
        // mission being aborted.
        if (key === 'm') {
          if (!onToggleManualRef.current || disabledRef.current('manual') !== null) return;
          e.preventDefault();
          runRef.current('manual');
          return;
        }
        // GO-TO is a mode toggle, not a one-shot command — arm pick mode here,
        // the map click sends the goto.
        if (key === 'g') {
          if (!visibleKeyRef.current('goto')) return;
          if (disabledRef.current('goto') !== null || !onToggleGotoRef.current) return;
          e.preventDefault();
          onToggleGotoRef.current();
          return;
        }
        if (key === 'a') action = 'arm';
        else if (key === 't') action = 'takeoff';
        else if (key === 'r') action = 'return_home';
        else if (key === 'l') action = 'land';
        else if (key === 'c') action = 'confirm_landing';
        else if (key === 'p') action = 'pause';
        else if (key === 'o') action = 'resume';
      }

      if (!action) return;
      if (!availability.canCommand) return;
      // Checked before the refusal, because a key that is not on the bar has no
      // refusal to give: `disabledReason` would return null for it and the
      // shortcut would fire a verb the operator cannot see. ABORT reaches this
      // line too and is never filtered (D14), so the emergency stop is
      // unaffected.
      if (!visibleKeyRef.current(action)) return;
      if (disabledRef.current(action) !== null) return;
      // The key itself is disabled while its command is in flight; the shortcut
      // has to be too, or mashing it POSTs the same command several times.
      // "In flight" is the whole ack lifecycle, not the POST window - see
      // `inFlight`, and #344, where 202s came back fast enough that the key was
      // live again between presses of a single gesture.
      // ABORT is exempt for the same reason its key is (see its call site): a
      // stalled POST must never be what stands between the operator and the
      // emergency stop. `e.repeat` above is what keeps a held key from
      // spamming it. LAND is exempt because L only opens the confirm dialog -
      // the one place LAND DIRECTLY lives while the orchestrated POST is held.
      if (action !== 'abort' && action !== 'land' && inFlightRef.current(action)) return;

      e.preventDefault();
      // An open confirm is not a reason to refuse an emergency stop. Closing it
      // cancels the launch it was asking about, which is what the operator
      // reaching for ABORT wants anyway.
      if (confirming !== null) setConfirming(null);
      runRef.current(action);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [availability.canCommand, confirming, onOpenDetails]);

  /**
   * Keyboard focus survives the key under it being removed by the phase.
   *
   * A keyboard operator tabs to CONFIRM LANDING during a hold-short; the
   * aircraft touches down; the button unmounts and focus falls to
   * `document.body`, so the next Tab restarts from the top of the document -
   * on a command surface, mid-recovery. Same on TAKEOFF at liftoff. Before
   * slice 10 the key stayed mounted and only changed state, so this could not
   * happen; `DroneMapControls` already carries the identical guard for the
   * conditional north key, which fails the same way.
   *
   * Deliberately narrow: it acts ONLY when focus actually landed on `body`,
   * which is what a removed element does and what nothing else does. It can
   * therefore never take focus away from somewhere it was wanted. ABORT is the
   * destination because D14 guarantees it is rendered in every phase.
   */
  const toolbarRef = useRef<HTMLDivElement>(null);
  const abortKeyRef = useRef<HTMLButtonElement>(null);
  const heldFocusRef = useRef(false);
  const visibleSignature = visibleKeys.map((k) => k.action).join(',');

  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const onIn = () => { heldFocusRef.current = true; };
    const onOut = (e: FocusEvent) => {
      // Only when focus left the toolbar for something OUTSIDE it. Moving
      // between two keys fires focusout with the next key as `relatedTarget`.
      if (e.relatedTarget instanceof Node && el.contains(e.relatedTarget)) return;
      heldFocusRef.current = false;
    };
    el.addEventListener('focusin', onIn);
    el.addEventListener('focusout', onOut);
    return () => {
      el.removeEventListener('focusin', onIn);
      el.removeEventListener('focusout', onOut);
    };
  }, []);

  useEffect(() => {
    if (!heldFocusRef.current) return;
    if (document.activeElement !== document.body) return;
    abortKeyRef.current?.focus();
  }, [visibleSignature]);

  return (
    <>
      <div
        ref={toolbarRef}
        role="toolbar"
        aria-label={`Commands for ${callsign}`}
        className="flex h-[60px] items-center gap-3 rounded-lg border border-subtle bg-card px-3"
      >
        {/* Which aircraft is about to receive the command. Target confirmation,
            not telemetry — the card carries the numbers. */}
        <div className="flex shrink-0 items-center gap-2">
          <span aria-hidden className="text-[13px] text-muted-foreground">▸</span>
          <div className="flex flex-col leading-none">
            <span className="text-[9.5px] uppercase tracking-[0.1em] text-muted-foreground">
              {availability.canCommand ? 'Commanding' : 'Selected'}
            </span>
            <span className="callsign mt-0.5 text-[15px] text-foreground">
              {callsign}
            </span>
          </div>
        </div>

        <span aria-hidden className="h-8 w-px shrink-0 bg-divider" />

        {availability.canCommand ? (
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
            <AckReadout command={command} />
            {/* Why the MANUAL key is dead, in words, on the surface itself.
                It used to live only in a `title` — which never fires on the
                touch device this feature targets, is unreachable by keyboard
                because the button is disabled, and is not part of the
                accessible name. The same treatment `blockedReason` gets on the
                right-hand branch of this ternary, and for the same reason: a
                disabled control with no explanation makes the operator guess. */}
            {manualKeyRefusal !== null && (
              <p
                data-testid="manual-key-refusal"
                className={`flex min-w-0 items-center gap-1.5 text-[10px] tracking-[0.04em] ${
                  manualRefusalIsPermanent ? 'text-muted-foreground' : 'text-status-amber-fg'
                }`}
              >
                <span aria-hidden>⃠</span>
                <span className="truncate">MANUAL — {manualKeyRefusal}</span>
              </p>
            )}
          </div>
        ) : (
          <p className="flex min-w-0 flex-1 items-center gap-2 text-[11px] tracking-[0.04em] text-muted-foreground">
            <span aria-hidden>⃠</span>
            <span className="truncate">{availability.blockedReason}</span>
          </p>
        )}

        {/* Sits beside the command lifecycle rather than on the state card: it
            is the answer to "why is the LAND I just pressed not finishing",
            which is a question about the command in flight. Renders in both
            branches - a viewer watching a hang is owed the reason too. */}
        {landing && (
          <span
            title={landing.title}
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-[0.09em]',
              landing.tone === 'green'
                ? 'border-status-green/40 bg-status-green/10 text-status-green-fg'
                : 'border-status-amber/50 bg-status-amber/15 text-status-amber-fg',
            )}
          >
            <span>{landing.label}</span>
            <span aria-hidden className="opacity-40">·</span>
            <span className="opacity-80">{landing.reason.replace(/_/g, ' ')}</span>
          </span>
        )}

        {/* The follow-up to a RETURN HOME the landing gate refused, AND said
            was safe to skip (#586). Never shown beside RETURN HOME itself -
            it exists because the gate can say no (dock emergency, low pack,
            a busy bay), and an operator then needs a way home that is not
            the same-looking button that got refused. Only for canCommand -
            a viewer or an unreachable drone gets neither key.

            Kept mounted through `emergencyRthSending` even after
            `rthRefused` clears, so a press has visible feedback rather than
            vanishing on the same tick it fires (see that state's own
            comment) - and NEVER disabled while sending, for the same reason
            ABORT and LAND DIRECT are reissuable while busy: `api.post`
            carries no timeout, and a control named "Emergency" must not be
            able to freeze itself unusable on a hung request. A duplicate
            direct send is harmless (`useDroneCommand`'s own per-drone lock
            already dedupes it); an unreachable one is not.

            `aria-live="assertive"` (an attribute, not a `role` - `role="alert"`
            would replace this control's native BUTTON role with an
            announce-only one, making it unreachable as a button) because this
            is a new safety-relevant control appearing on a bar with no other
            mechanism to announce it, and the reason is rendered as VISIBLE
            text rather than only a `title` - the MANUAL key's own refusal
            three blocks up documents why a hover-only tooltip is not enough
            on the touch device this bar targets. */}
        {availability.canCommand && (rthRefused || emergencyRthSending) && (
          <button
            type="button"
            onClick={() => {
              const forDroneId = drone.drone_id;
              setEmergencyRthSending(true);
              void command.send('return_home', { direct: true })
                .finally(() => {
                  // A stale send for a drone the operator has since switched
                  // away from must not clear the busy state of a FRESH
                  // Emergency RTH fired for whichever drone is selected now.
                  if (emergencyRthDroneIdRef.current === forDroneId) setEmergencyRthSending(false);
                });
            }}
            // What the control DOES (the static hint) ahead of why it appeared
            // (the server's specific reason, when there still is one) - a
            // first-time reader needs both, and only the reason fits as the
            // always-visible line below.
            title={[DIRECT_VARIANT.emergencyRthHint, displayedRthMessage].filter(Boolean).join(' ')}
            aria-live="assertive"
            className="flex min-h-10 shrink-0 flex-col items-start justify-center gap-0.5 rounded-md border border-status-red/40 bg-status-red/10 px-2.5 py-1 text-status-red-fg hover:bg-status-red/15"
          >
            <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider">
              {emergencyRthSending
                ? <Loader2 className="size-3 animate-spin" strokeWidth={2} />
                : <AlertTriangle className="size-3" strokeWidth={1.5} />}
              {DIRECT_VARIANT.emergencyRth}
            </span>
            {displayedRthMessage && (
              <span className="max-w-[220px] truncate text-[10px] normal-case tracking-normal opacity-80">
                {rthRefusalReason(displayedRthMessage)}
              </span>
            )}
          </button>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {availability.canCommand ? (
            <>
              {visibleKeys.map((k) => {
                const isGoto = k.action === 'goto';
                const isManual = k.action === 'manual';
                return (
                  <CommandKey
                    key={k.action}
                    label={k.label}
                    hint={k.hint}
                    danger={k.danger}
                    // The manual key carries its authority state as a WORD in
                    // place of the shortcut hint. Fixed width, so IDLE and
                    // OVERRIDE do not move every key to its right.
                    statusWord={isManual ? manual?.label : undefined}
                    wide={isManual}
                    active={isGoto ? gotoArmed : isManual ? manualHolds : undefined}
                    alarm={isManual && (manual?.state === 'OVERRIDE' || manual?.state === 'FAULT')}
                    attention={
                      // #264's per-verb prompt, kept behind this branch's
                      // manual guard: while manual authority is held ARM is
                      // disabled with its own reason, so prompting for it would
                      // point the operator at a key that cannot answer.
                      k.action === 'arm' && heldAwaitingArm && !manualHolds
                        ? HELD_ARM_PROMPT[heldAwaitingArm]
                        : null
                    }
                    disabledReason={disabledReason(k.action)}
                    busy={
                      // Inline, not `isManual`: an aliased boolean does not
                      // narrow `k.action` off 'manual' for `inFlight`.
                      k.action === 'manual'
                        ? (manual?.busy ?? false)
                        : inFlight(k.action)
                    }
                    // MANUAL is a mode toggle, so it stays pressable while
                    // its own request is in flight - the operator must always
                    // be able to hand authority back.
                    // LAND stays pressable while its orchestrated POST is held
                    // (the bay wait can run 45 s): pressing it only opens the
                    // confirm dialog, and that dialog is where LAND DIRECTLY
                    // lives - the escape hatch must not be locked behind the
                    // exact hang it exists for. The duplicate orchestrated
                    // send is still refused by the hook's lock (and the
                    // confirm key disables itself, below).
                    reissuableWhileBusy={isManual || k.action === 'land'}
                    onClick={isGoto ? () => onToggleGoto?.() : () => run(k.action)}
                  />
                );
              })}
              <span aria-hidden className="mx-1 h-8 w-px bg-divider" />
              {/* Stays pressable while its own POST is in flight. `api.post`
                  carries no timeout, so a stalled request would otherwise hold
                  the key disabled for as long as the browser waits — leaving no
                  way to re-issue the emergency stop on exactly the degraded
                  link that caused the stall. A duplicate abort is harmless; an
                  unreachable one is not. */}
              <CommandKey
                buttonRef={abortKeyRef}
                label={COMMAND_KEY_LABEL.abort}
                hint="⇧⌫"
                danger
                disabledReason={disabledReason('abort')}
                busy={inFlight('abort')}
                reissuableWhileBusy
                onClick={() => run('abort')}
              />
            </>
          ) : (
            availability.dockId && onOpenDock && (
              <CommandKey
                label="OPEN DOCK"
                onClick={() => onOpenDock(availability.dockId as string)}
              />
            )
          )}

          <span aria-hidden className="mx-1 h-8 w-px bg-divider" />
          <CommandKey label="⊞ DETAILS" hint="D" active={isDetailsOpen} onClick={onOpenDetails} />
        </div>
      </div>

      <Dialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        {/* The takeoff dialog is ~2.5x taller than the others now that it holds
            a picker. Without a cap it overflows a short landscape viewport (an
            RC screen or a tablet) and, because DialogContent is centred and does
            not scroll, Cancel and Confirm land off both edges at once. */}
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[13px] uppercase tracking-wider text-status-red-fg">
              <AlertTriangle className="size-4" strokeWidth={1.5} />
              {CONFIRM_DIALOG.droneTitle}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            <span className="callsign text-foreground">{callsign}</span>{' '}
            {confirming === 'manual'
              ? `is flying ${missionName?.trim() || 'a mission'}. Taking manual control aborts it — the RC switches to the sticks and the wayline stops.`
              : confirming === 'takeoff'
              ? altitudeSettable
                ? isCompanion
                  // The companion HOLDS the takeoff until an operator arms — a
                  // COP ARM press or the RC button (MissionController). The
                  // altitude IS real (it flies as a wayline climb), so state the
                  // number; what SHOVRON cannot do is start the launch itself,
                  // and a confirm dialog must not promise the arming step.
                  //
                  // This branch is why the old `isCompanion` copy never rendered:
                  // it sat under `!altitudeSettable`, which requires an unknown
                  // vendor — and an unknown vendor cannot be the companion. The
                  // honest sentence was unreachable on exactly the aircraft it
                  // was written for.
                  ? `will climb to ${formatAltitude(takeoffAltitudeM, unit)} AGL once armed — the companion app holds the takeoff until ARM.`
                  : `will arm and climb to ${formatAltitude(takeoffAltitudeM, unit)} AGL.`
                // No registry row, so the vendor is unknown and neither the
                // altitude nor the arming behaviour can be attributed.
                : 'will arm and climb.'
              : confirming === 'confirm_landing'
                ? confirmLandingCopy(landing?.reason ?? null)
                : confirming
                  ? CONFIRM_COPY[confirming] ?? 'will execute this command.'
                  : ''}
          </p>

          {confirming === 'takeoff' && (altitudeSettable ? (
            <>
              {/* The same control the dock launch card uses, so an operator who
                  has set a launch altitude once has already learned this one.
                  showRth is off: a hand takeoff sets no return altitude, exactly
                  as a point launch doesn't. */}
              <AltitudeControl
                profile={altitudeProfile}
                authority={ceilingAuthority}
                valueMeters={takeoffAltitudeM}
                onChange={(meters) => {
                  takeoffTouchedRef.current = true;
                  setTakeoffAltitudeM(meters);
                }}
                unit={unit}
                onUnitChange={setUnit}
                showRth={false}
                reference="AGL from launch point"
                label="Takeoff altitude"
              />
              {/* role="status" so crossing the ceiling on the slider is
                  announced, and bordered/iconed so the state is never carried
                  by colour alone. */}
              {ceilingNotice.message && (
                <p
                  role="status"
                  aria-live="polite"
                  className="flex items-start gap-1.5 rounded-md border border-status-amber/40 bg-status-amber/10 p-2 text-[10px] leading-relaxed text-status-amber-fg"
                >
                  <AlertTriangle className="mt-px size-3 shrink-0" strokeWidth={1.5} />
                  <span>{ceilingNotice.message}</span>
                </p>
              )}
              {/* The numbers above are platform defaults, not this org's
                  configuration. Say so rather than letting them read as policy —
                  'loading' matters as much as 'error', because a cold COP load
                  is exactly when an operator reaches for TAKEOFF. */}
              {altitudeProfileStatus !== 'ready' && (
                <p className="text-[10px] leading-relaxed text-muted-foreground">
                  {altitudeProfileStatus === 'loading'
                    ? 'Loading organization altitude profile — values above are platform defaults.'
                    : 'Org altitude configuration unavailable — showing platform defaults.'}
                </p>
              )}
            </>
          ) : (
            // Never say only "the altitude cannot be set". Something still flies:
            // the API substitutes 10 m when no altitude is sent, and an operator
            // confirming a launch is owed the number that will actually be
            // commanded, not the absence of one.
            //
            // No isCompanion arm here: this block only renders when
            // `!altitudeSettable`, i.e. no known route — and an unknown route
            // cannot be the companion. The DJI variant that used to
            // sit here was unreachable, and stale besides (the companion flies
            // the altitude as a wayline climb now, so "can neither set nor read
            // it" stopped being true).
            <p className="rounded-md border border-subtle surface-elevated p-2.5 text-[10px] leading-relaxed text-muted-foreground">
              {`Aircraft type not yet known, so no altitude can be chosen — SHOVRON will command the ${formatAltitude(FALLBACK_TAKEOFF_ALTITUDE_M, unit)} default. Reload the fleet registry to set one.`}
            </p>
          ))}

          <DialogFooter className="gap-2">
            {/* Focus lands here rather than on the picker's first control: the
                dialog is opened by the T shortcut, and autofocusing the unit
                toggle meant a follow-up Enter/Space silently flipped ft/m on a
                command surface. Cancel is the safe landing. */}
            <button
              type="button"
              autoFocus
              onClick={() => setConfirming(null)}
              className="h-9 flex-1 rounded-md border border-subtle bg-muted/40 text-[11px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            {/* The smoke-test hop, offered beside the real launch: motors up to
                a low hover and nothing else — no wayline, no mission row. The
                runbook asks for exactly this before any sortie. */}
            {confirming === 'takeoff' && (
              <button
                type="button"
                onClick={() => {
                  setConfirming(null);
                  void command.send('takeoff', { altitude_m: HOP_ALTITUDE_M });
                }}
                title="Motors up to a low hover only — no mission, no climb"
                className="h-9 flex-1 rounded-md border border-subtle bg-muted/40 text-[11px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground"
              >
                Low hop
              </button>
            )}
            {/* The escape hatch that makes the dock landing sequence safe to
                have at all. Every refusal the server can raise (a bay that
                never confirms, a dock in emergency, a pack too low to afford
                the wait) refuses the ORCHESTRATED landing only - this key is
                the direct one, and it is offered before the refusal rather
                than only after it, so an operator watching something go wrong
                never has to discover it exists. */}
            {confirming === 'land' && (
              <button
                type="button"
                onClick={() => {
                  setConfirming(null);
                  void command.send('land', { direct: true });
                }}
                title={DIRECT_VARIANT.landHint}
                className="h-9 flex-1 rounded-md border border-subtle bg-muted/40 text-[11px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground"
              >
                {DIRECT_VARIANT.land}
              </button>
            )}
            {/* The LAUNCH twin of LAND DIRECTLY, and it earns its place the same
                way. Since #403 a TAKEOFF on a docked aircraft opens the bay and
                waits for the dock to confirm - so it can now be REFUSED, and
                its 409 names the direct path. Any dock whose board never
                reports tray position refuses every takeoff (BAY_NO_SIGNAL_MS
                names M-011 as exactly that), which without this button would
                mean a manual launch is simply unavailable from the COP.

                Offered before the refusal, not after it, for the reason the
                land twin gives: an operator watching something go wrong should
                never have to discover the escape exists. */}
            {confirming === 'takeoff' && (
              <button
                type="button"
                onClick={() => {
                  setConfirming(null);
                  void command.send('takeoff', {
                    ...(altitudeSettable ? { altitude_m: takeoffAltitudeM } : {}),
                    direct: true,
                  });
                }}
                title="Take off without opening the bay - only if you can SEE the tray is fully out"
                className="h-9 flex-1 rounded-md border border-subtle bg-muted/40 text-[11px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground"
              >
                Take off directly
              </button>
            )}
            <button
              type="button"
              // A LAND already in flight makes this exact send a silent no-op
              // in the hook - so the key says so instead of pretending. LAND
              // DIRECTLY beside it stays live: that is the reason the dialog
              // can be reopened mid-hold at all.
              disabled={confirming === 'land' && inFlight('land')}
              onClick={() => {
                const action = confirming;
                setConfirming(null);
                if (!action) return;
                if (action === 'manual') {
                  onToggleManual?.();
                  return;
                }
                // Only takeoff carries an altitude, and only where the vendor
                // plane actually flies it — sending one to a companion drone
                // would put a number on the wire that changes nothing. Every
                // other command dispatches exactly as it did before.
                if (action === 'takeoff' && altitudeSettable) {
                  void command.send(action, { altitude_m: takeoffAltitudeM });
                  return;
                }
                void command.send(action);
              }}
              // Above the ceiling the button itself carries the state. The same
              // control for a routine and an over-ceiling launch is how a
              // warning gets clicked past at 02:00 — the dock launch card makes
              // exactly this argument, and this dialog has to hold the line too.
              className={cn(
                'h-9 flex-1 rounded-md border text-[11px] font-bold uppercase tracking-[0.09em] disabled:cursor-not-allowed disabled:opacity-40',
                confirmOverCeiling
                  ? 'border-status-amber/40 bg-status-amber/10 text-status-amber-fg hover:bg-status-amber/15'
                  : 'border-status-red/40 bg-status-red/10 text-status-red-fg hover:bg-status-red/15',
              )}
            >
              {confirmLabel}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * How far a lifecycle step has got.
 *
 * `unconfirmed` is the rung between the other two and exists so the readout can
 * distinguish "the device did something" from "the aircraft answered". It is
 * deliberately dimmer than `reached` and brighter than `idle`, and it never uses
 * the green the exec step uses — an operator scanning the bar reads colour before
 * text.
 */
type StepState = 'idle' | 'unconfirmed' | 'reached';

/** What fits on one rung of a 60px toolbar beside three other rungs. */
const PROGRESS_MAX_CHARS = 24;

/**
 * The aircraft's progress sentence, cut to fit a rung.
 *
 * The device's own words, uppercased into the bar's voice rather than re-worded
 * here — the RC is the authority on what it is doing, and a COP that paraphrases
 * it can only ever be wrong in a new way. The full text stays on the element's
 * title, so nothing is lost, only shortened.
 */
function progressText(detail: string): string {
  const text = detail.toUpperCase();
  return text.length <= PROGRESS_MAX_CHARS ? text : `${text.slice(0, PROGRESS_MAX_CHARS - 1)}…`;
}

const STEP_CLASS: Record<StepState, (key: string) => string> = {
  idle: () => 'text-muted-foreground',
  unconfirmed: () => 'text-status-amber-fg/55',
  reached: (key) => (key === 'exec' ? 'text-status-green-fg' : 'text-status-amber-fg'),
};

/**
 * The command lifecycle — the bar's only readout, and about the command rather
 * than the aircraft, which is why it belongs here and not on the card.
 */
function AckReadout({ command }: { command: DroneCommandController }) {
  // One command at a time, first match wins — so ABORT leads. An abort is
  // almost always issued *out of* another command that is still lit here (an
  // acked RTH holds its entry for up to 75 s), and without this the readout
  // would spend that time advertising the command the abort just superseded
  // while the abort's own lifecycle went unrendered.
  // confirm_landing sits second for the same reason: it is only ever issued out
  // of a LAND that is still lit, and an acked LAND holds its entry for 75 s.
  const inFlight = (['abort', 'confirm_landing', 'arm', 'disarm', 'takeoff', 'return_home', 'land', 'pause', 'resume', 'goto'] as DroneCommandAction[])
    .map((action) => ({ action, phase: command.phaseOf(action), status: command.statusOf(action) }))
    .find((entry) => entry.phase !== null);

  if (!inFlight) {
    // A LAND POST can be HELD server-side while the bay travels (up to 45 s)
    // - there is no ack entry yet, and an empty readout for that long reads
    // as a hang with no cause, the exact silence this readout exists to kill.
    if (command.submitting === 'land') {
      return (
        <div
          className="flex min-w-0 flex-1 items-center gap-2 text-[11px] tracking-wide"
          aria-live="polite"
        >
          <span className="shrink-0 font-bold text-foreground">LAND</span>
          <span
            className="truncate text-status-amber-fg"
            title="A dock landing is released only once the dock confirms the bay open - the command is held until then"
          >
            ◷ HOLDING - A DOCK LANDING WAITS FOR THE BAY TO CONFIRM
          </span>
        </div>
      );
    }
    return <div className="min-w-0 flex-1" aria-live="polite" />;
  }

  const { action, phase, status } = inFlight;
  const label = action.replace(/_/g, ' ').toUpperCase();
  const failed = phase === 'failed';
  const detail = command.failureOf(action);
  const progress = command.progressOf(action);

  // `held` and `dispatched` are the device talking about itself: the companion
  // app is sitting on the command awaiting an operator ARM, or it invoked an SDK
  // method and it returned. Neither is the AIRCRAFT acknowledging anything.
  // Lighting the ACK step for them renders "the aircraft acknowledged" for a
  // command it may have refused — the exact claim GAP-P0-04 exists to remove — so
  // they get their own weaker rung instead.
  const unconfirmed = isUnconfirmedStatus(status);
  const steps: Array<{ key: string; text: string; state: StepState; title?: string }> = [
    { key: 'sent', text: '◷ SENT', state: 'reached' },
    {
      key: 'ack',
      text: status === 'held' ? '◇ HELD' : unconfirmed ? '◇ DISPATCHED' : '◈ ACK',
      state: phase === 'pending' ? 'idle' : unconfirmed ? 'unconfirmed' : 'reached',
      title: status === 'held'
        ? 'Held by the companion app for an operator ARM — not sent to the aircraft'
        : unconfirmed
          ? 'SDK method invoked — the aircraft has not answered yet'
          : undefined,
    },
    {
      key: 'exec',
      // The aircraft's own account of what it is doing, when it has one. A
      // wayline reports its waypoints here ("waypoint 3 of 5"), which is the
      // whole difference between watching a multi-leg route progress and
      // watching an unchanging EXECUTING for the length of a mission.
      text: status === 'done' || phase === 'done'
        ? '✓ DONE'
        : progress
          ? `✓ ${progressText(progress)}`
          : '✓ EXECUTING',
      state: phase === 'done' || status === 'executing' ? 'reached' : 'idle',
      title: progress ?? undefined,
    },
  ];

  return (
    <div
      className="flex min-w-0 flex-1 items-center gap-2 text-[11px] tracking-wide"
      aria-live="polite"
    >
      <span className="shrink-0 font-bold text-foreground">{label}</span>
      {failed ? (
        <span className="truncate text-status-red-fg">✗ {detail || 'REJECTED'}</span>
      ) : (
        <span className="flex items-center gap-1.5">
          {steps.map((s, i) => (
            <span key={s.key} className="flex items-center gap-1.5">
              {i > 0 && <span aria-hidden className="text-border">›</span>}
              <span title={s.title} className={cn(STEP_CLASS[s.state](s.key))}>
                {s.text}
              </span>
            </span>
          ))}
        </span>
      )}
    </div>
  );
}

function CommandKey({
  label,
  hint,
  statusWord,
  danger = false,
  active = false,
  alarm = false,
  wide = false,
  busy = false,
  reissuableWhileBusy = false,
  disabledReason = null,
  attention = null,
  buttonRef,
  onClick,
}: {
  label: string;
  hint?: string;
  /**
   * A live state word shown in place of the shortcut hint — for a key whose
   * meaning changes with the aircraft rather than staying constant. Rendered as
   * TEXT, never as colour alone, and included in the accessible name so a
   * screen-reader operator hears "MANUAL, LIVE" rather than just "MANUAL".
   */
  statusWord?: string;
  danger?: boolean;
  active?: boolean;
  /** Red, for a state the operator did not choose (OVERRIDE, FAULT). */
  alarm?: boolean;
  /** Fixed wider footprint, so a changing status word cannot move its neighbours. */
  wide?: boolean;
  busy?: boolean;
  /** Show the spinner but stay pressable — see the ABORT key's call site. */
  reissuableWhileBusy?: boolean;
  disabledReason?: string | null;
  /** Amber "press me" state with its reason — a held command waiting on this key. */
  attention?: string | null;
  /**
   * Only ABORT passes one, and only so the bar can put focus somewhere when the
   * phase removes the key a keyboard operator was standing on. ABORT is the
   * destination because D14 guarantees it is rendered in every phase.
   */
  buttonRef?: Ref<HTMLButtonElement>;
  onClick: () => void;
}) {
  const disabled = disabledReason !== null || (busy && !reissuableWhileBusy);
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={statusWord ? `${label} — ${statusWord}` : undefined}
      // The reason rides on the control itself, so hovering a dead key explains
      // it rather than leaving the operator to guess.
      title={disabledReason ?? attention ?? undefined}
      className={cn(
        'flex h-10 flex-col items-center justify-center gap-0.5 rounded-md border px-2.5',
        wide ? 'min-w-[92px]' : 'min-w-[72px]',
        danger
          ? 'border-status-red/40 bg-status-red/10 text-status-red-fg hover:bg-status-red/15'
          : alarm
            ? 'border-status-red/50 bg-status-red/15 text-status-red-fg hover:bg-status-red/25'
            : attention !== null && !disabled
              ? 'border-status-amber/50 bg-status-amber/15 text-status-amber-fg hover:bg-status-amber/20'
              : active
                ? 'border-status-blue/40 bg-status-blue/15 text-status-blue-fg'
                : 'border-subtle bg-muted/40 text-muted-foreground hover:bg-accent hover:text-foreground',
        disabled && 'cursor-not-allowed opacity-30 hover:bg-transparent',
      )}
    >
      <span className="flex items-center gap-1 text-[11px] font-bold tracking-wider">
        {busy && <Loader2 className="size-3 animate-spin" strokeWidth={2} />}
        {label}
      </span>
      {/* Both, never one instead of the other. A status word used to REPLACE the
          shortcut, so the MANUAL key was the one key on the bar that never
          showed its letter — an operator could not learn M from the surface
          that offers it. */}
      {(statusWord || hint) && (
        <span className="flex items-center gap-1 text-[9px] tracking-wide">
          {statusWord && <span>{statusWord}</span>}
          {hint && <kbd className="text-[9px] tracking-wide text-muted-foreground">{hint}</kbd>}
        </span>
      )}
    </button>
  );
}
